/** Mask materialization: derive a Fabric clipPath's `effective`
 *  alpha canvas from the user-painted `raw` canvas + the LayerMask
 *  config (enabled / inverted / density / feather).
 *
 *  Background. Tier 1 Group A shipped raster masks via Fabric's
 *  native `clipPath`. Fabric's clipPath compositing is `destination-
 *  in` against the clipPath's alpha — so it already does soft
 *  clipping correctly. We just need to bake the LayerMask config
 *  into a separate effective alpha canvas; the user's painted strokes
 *  stay on the raw canvas, and the effective canvas is rederived
 *  whenever raw or config changes.
 *
 *  This lets the slow-path compositor in `render-pipeline.ts` stay
 *  unchanged — `paintLeaf` calls `fabricObj.render(ctx)` which honors
 *  the leaf's clipPath, so adjustment-clipping-to-a-feathered-leaf
 *  picks up the soft alpha automatically.
 *
 *  Performance. `feather` runs as a single canvas2d `filter: blur(N)`
 *  pass (GPU-accelerated). `invert` and `density` collapse into one
 *  getImageData + per-pixel loop + putImageData round-trip (~80ms on
 *  a 4K mask). When config is all-default (enabled, !inverted,
 *  feather=0, density=1), materialization is a single drawImage —
 *  effectively free.
 *
 *  Order of operations is fixed: feather (blur the raw alpha) → invert
 *  (255 - a) → density (lerp toward 255: density*a + (1-density)*255).
 *  This matches Photoshop's "Density 0% = mask off, Density 100% =
 *  mask at full strength" semantic — at density=0, every alpha
 *  collapses to 255 (no clipping), at density=1 the alpha is the
 *  feathered/inverted raw. Invert before density so the "fade toward
 *  fully revealed" makes sense for an inverted mask too. */

import type { LayerMask } from '../types';

export interface MaskMaterializationConfig {
  enabled: boolean;
  inverted: boolean;
  /** 0..1. Photoshop semantic: 1 = full mask effect, 0 = mask off
   *  (effective alpha collapses to 255 everywhere, layer fully
   *  revealed). */
  density: number;
  /** Pixel radius of a Gaussian blur applied to the raw alpha before
   *  invert/density. 0 = no blur. */
  feather: number;
}

/** Project a `LayerMask` to the materialization-relevant fields.
 *  `linked` and ids are runtime metadata, not pixel-relevant. */
export function maskMaterializationConfig(mask: LayerMask): MaskMaterializationConfig {
  return {
    enabled: mask.enabled,
    inverted: mask.inverted,
    density: clamp01(mask.density),
    feather: Math.max(0, mask.feather),
  };
}

/** True when the mask config produces output equal to the raw input.
 *  Callers can fast-path with `target.drawImage(source)` in this
 *  case, skipping the blur filter and per-pixel pass. */
export function isMaskIdentity(config: MaskMaterializationConfig): boolean {
  return config.enabled && !config.inverted && config.feather === 0 && config.density === 1;
}

/** Bake `source` + `config` into `target`'s alpha. Caller owns both
 *  canvases; both must be the same size as `width` × `height`. The
 *  target is cleared before writing.
 *
 *  Disabled masks resolve to a fully-opaque white fill — Fabric's
 *  clipPath sees alpha=255 everywhere and the layer renders
 *  unclipped, which is the desired "mask off" visual. */
export function materializeEffectiveMaskInto(
  target: HTMLCanvasElement,
  source: HTMLCanvasElement,
  config: MaskMaterializationConfig,
  width: number,
  height: number,
): void {
  const ctx = target.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.filter = 'none';
  ctx.clearRect(0, 0, width, height);

  if (!config.enabled) {
    // Layer fully revealed — clipPath alpha = 255 everywhere.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    return;
  }

  if (isMaskIdentity(config)) {
    // Pure passthrough — common case (no slow-path features).
    ctx.drawImage(source, 0, 0);
    ctx.restore();
    return;
  }

  // Step 1: feather. canvas2d `filter` runs on the GPU when
  // available; falls back to a CPU box blur in headless / older
  // browsers. We toggle the filter off again after drawing so the
  // subsequent putImageData isn't accidentally re-blurred.
  if (config.feather > 0) {
    ctx.filter = `blur(${config.feather}px)`;
    ctx.drawImage(source, 0, 0);
    ctx.filter = 'none';
  } else {
    ctx.drawImage(source, 0, 0);
  }

  const needsInvert = config.inverted;
  const needsDensity = config.density < 1;
  if (!needsInvert && !needsDensity) {
    ctx.restore();
    return;
  }

  // Step 2: invert + density in one per-pixel pass. Splitting into
  // two passes would double the getImageData/putImageData cost.
  const id = ctx.getImageData(0, 0, width, height);
  applyInvertDensityToImageData(id, needsInvert, config.density);
  ctx.putImageData(id, 0, 0);
  ctx.restore();
}

/** In-place invert + density on an ImageData's alpha channel. Public
 *  for the unit-test path — happy-dom's canvas doesn't carry blur
 *  fidelity but `applyInvertDensityToImageData` is pure math and
 *  tests against it without bouncing through a canvas context. */
export function applyInvertDensityToImageData(
  id: ImageData,
  invert: boolean,
  density: number,
): void {
  const d = id.data;
  const lerp = density < 1;
  const oneMinus = 1 - density;
  for (let i = 0; i < d.length; i += 4) {
    let a = d[i + 3];
    if (invert) a = 255 - a;
    if (lerp) a = Math.round(density * a + oneMinus * 255);
    d[i + 3] = a;
    // RGB irrelevant to clipPath compositing (uses alpha only) but
    // we normalize to white so downstream inspection / debug tooling
    // sees a sensible alpha-mask color.
    d[i] = 255;
    d[i + 1] = 255;
    d[i + 2] = 255;
  }
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 1;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
